import { useState, useEffect, useMemo } from 'react';
import { Modal } from '@mui/material';

import { SupplierJobApiResponse, SupplierJobUpdate } from '@dill/dill-shared';

import { openDillToast } from '../../../utils/helpers';

import {
  getSupplierBuyers,
  updateSupplierBuyer,
} from '../../../redux/services/supplierBuyersService';
import {
  createSupplierJob,
  getSupplierJob,
  updateSupplierJob,
} from '../../../redux/services/supplierJobsService';

import './AppNewSupplierJobModal.scss';

import { useAppDispatch, useAppSelector } from '../../../redux/store';
import AddSupplierJobForm from './AddSupplierJobForm';
import {
  ContructionMonitorPermitOption,
  fetchQueuryJobSiteAddresses,
  VerifyJobRequestData,
} from '../../../redux/services/verifyAddressService';

import stringSimilarity from 'string-similarity';
import SupplierVerifyJobForm from './SupplierVerifyJobForm';
import { SupplierVerifiedJobFormData } from './constants';
import SupplierCompareJob from './SupplierCompareJob';
import CompareJobData from './CompareJobData';
import RequestInfoSheetForm from './RequestInfoSheetForm';
import {
  getRequestedSupplierJobs,
  updateRequestedSupplierJob,
} from '../../../redux/services/requestJobInformationService';

type PartialSupplierJobUpdatePick = Pick<
  SupplierJobUpdate,
  | 'name'
  | 'jobNumber'
  | 'customerJobNumber'
  | 'jobAddress'
  | 'jobCity'
  | 'jobState'
  | 'jobZipCode'
  | 'owners'
  | 'GCs'
  | 'buyerId'
  | 'lenders'
  | 'constructionMonitorPermitId'
  | 'apn'
  | 'estimatedMaterialAmount'
  | 'jobType'
  | 'jobSector'
  | 'customerType'
  | 'jobFurnishings'
  | 'hasNoLender'
  | 'jobCounty'
  | 'jobLegalDescription'
  | 'isJobBonded'
  | 'sureties'
  | 'bondNumber'
  | 'jobInfoSheetAttachments'
  | 'isGCUnknown'
  | 'isOwnerUnknown'
  | 'isLenderUnknown'
  | 'isSurityUnknown'
>;

enum ModalState {
  ADD_JOB,
  EDIT_JOB,
  VERIFY_JOB,
  COMPARE_DATA,
  VERIFIED_JOB_DATA,
  REQUEST_JOB_INFO_SHEET,
}

export const AppNewSupplierJobModal = ({
  open,
  handleClose,
  editMode = false,
  selectedSupplierJob,
  viewState = ModalState.ADD_JOB,
  onReload,
  onUpdateRequestedJob = () => {},
}: {
  open: boolean;
  handleClose: (data: { wasBuyerEdited: boolean }) => void;
  editMode?: boolean;
  selectedSupplierJob?: SupplierJobApiResponse | null;
  viewState?: ModalState;
  onReload?: () => void;
  onUpdateRequestedJob?: () => void;
}) => {
  const INITIAL_STATE = {
    name: '',
    jobNumber: '',
    customerJobNumber: '',
    jobAddress: '',
    jobCity: '',
    jobState: '',
    jobZipCode: '',
    owners: [
      {
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
      },
    ],
    GCs: [
      {
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
      },
    ],
    buyerId: '',
    lenders: [
      {
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
      },
    ],
    constructionMonitorPermitId: null,
    apn: '',
    estimatedMaterialAmount: 0,
    jobType: 'commercial',
    jobSector: 'private',
    customerType: 'SUB' as const,
    jobFurnishings: '',
    hasNoLender: false,
    isJobBonded: false,
    sureties: [
      {
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        bondNumber: '',
      },
    ],
    bondNumber: '',
    isGCUnknown: false,
    isLenderUnknown: false,
    isOwnerUnknown: false,
    isSurityUnknown: false,
  } as PartialSupplierJobUpdatePick;

  const [verifyJobData, setVerifyJobData] = useState<SupplierVerifiedJobFormData>(
    {} as SupplierVerifiedJobFormData
  );
  const [originalJobData, setOriginalJobData] = useState<SupplierVerifiedJobFormData>(
    {} as SupplierVerifiedJobFormData
  );
  const [constructionMonitorData, setConstructionMonitorJobData] =
    useState<SupplierVerifiedJobFormData>({} as SupplierVerifiedJobFormData);

  const dispatch = useAppDispatch();

  const [supplierJobInput, setSupplierJobInput] = useState(INITIAL_STATE);
  const [requestJobInfoSheet, setRequestJobInfoSheet] = useState({
    ...INITIAL_STATE,
    buyerName: '',
    buyerAddress: '',
    buyerCity: '',
    buyerState: '',
    buyerZipCode: '',
  });

  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { requestedSupplierJobs } = useAppSelector((state) => state.requestJobInformation);

  const [modalState, setModalState] = useState<ModalState>(viewState);
  const [wasBuyerEdited, setWasBuyerEdited] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyJobCheckBox, setVerifyJobCheckBox] = useState(false);

  useEffect(() => {
    if (open && user) {
      dispatch(getSupplierBuyers({ supplierId: currentSupplier?.id || '' }));
      setWasBuyerEdited(false);
      setVerifyJobCheckBox(false);
    }
  }, [open, user?.id]);

  useEffect(() => {
    if (selectedSupplierJob && open) {
      let moreDetails = {};
      if (selectedSupplierJob.jobState === 'AZ') {
        moreDetails = {
          ...moreDetails,
          jobCounty: selectedSupplierJob.jobCounty ?? '',
          jobLegalDescription: selectedSupplierJob.jobLegalDescription ?? '',
        };
      }
      if (selectedSupplierJob.jobInfoSheetAttachments) {
        moreDetails = {
          ...moreDetails,
          jobInfoSheetAttachments: selectedSupplierJob.jobInfoSheetAttachments,
        };
      }
      setSupplierJobInput({
        name: selectedSupplierJob.name ?? '',
        jobNumber: selectedSupplierJob.jobNumber ? String(selectedSupplierJob.jobNumber) : '',
        customerJobNumber: selectedSupplierJob?.customerJobNumber ?? '',
        jobAddress: selectedSupplierJob.jobAddress ?? '',
        jobCity: selectedSupplierJob.jobCity ?? '',
        jobState: selectedSupplierJob.jobState ?? '',
        jobZipCode: selectedSupplierJob.jobZipCode ? String(selectedSupplierJob.jobZipCode) : '',
        owners: selectedSupplierJob.owners ?? [],
        GCs: selectedSupplierJob.GCs ?? [],
        buyerId: selectedSupplierJob.buyerId ?? '',
        lenders: selectedSupplierJob.lenders ?? [],
        constructionMonitorPermitId: selectedSupplierJob.constructionMonitorPermitId,
        apn: selectedSupplierJob.apn ?? '',
        estimatedMaterialAmount: selectedSupplierJob.estimatedMaterialAmount
          ? Number(selectedSupplierJob.estimatedMaterialAmount)
          : 0,
        jobType: selectedSupplierJob.jobType || 'commercial',
        jobSector: selectedSupplierJob.jobSector || 'private',
        customerType: selectedSupplierJob.customerType ?? 'SUB',
        jobFurnishings: selectedSupplierJob.jobFurnishings ?? '',
        hasNoLender: selectedSupplierJob.hasNoLender ?? false,
        isJobBonded: selectedSupplierJob.isJobBonded ?? false,
        sureties: selectedSupplierJob.sureties ?? [],
        jobInfoSheetAttachments: selectedSupplierJob?.jobInfoSheetAttachments || [],
        isGCUnknown: selectedSupplierJob?.isGCUnknown,
        isLenderUnknown: selectedSupplierJob?.isLenderUnknown,
        isOwnerUnknown: selectedSupplierJob?.isOwnerUnknown,
        isSurityUnknown: selectedSupplierJob?.isSurityUnknown,
        ...moreDetails,
      });
    }
  }, [open, selectedSupplierJob]);

  const refetchSupplierJob = async () => {
    if (selectedSupplierJob) {
      await dispatch(
        getSupplierJob({
          supplierId: currentSupplier?.id || '',
          supplierJobId: selectedSupplierJob?.id,
        })
      );
    }
  };

  const handleCloseModal = () => {
    handleClose({ wasBuyerEdited });
    setSupplierJobInput(INITIAL_STATE);
    setModalState(ModalState.ADD_JOB);
    setSupplierJobInput({} as SupplierVerifiedJobFormData);
  };

  const handleSubmitJob = async ({
    formData,
    attachments,
  }: {
    formData: SupplierVerifiedJobFormData;
    attachments?: { attachmentFiles: File[]; attachmentUrls: { name: string; url: string }[] };
  }) => {
    let moreDetails = {};
    if (formData.jobState === 'AZ') {
      moreDetails = {
        ...moreDetails,
        jobCounty: formData.jobCounty ?? '',
        jobLegalDescription: formData.jobLegalDescription ?? '',
      };
    }
    if (selectedSupplierJob && selectedSupplierJob.jobInfoSheetAttachments) {
      moreDetails = {
        ...moreDetails,
        jobInfoSheetAttachments: selectedSupplierJob.jobInfoSheetAttachments,
      };
    }
    const data = {
      supplierId: currentSupplier?.id || '',
      name: formData.name || '',
      jobNumber: formData.jobNumber || '',
      customerJobNumber: formData.customerJobNumber || '',
      jobAddress: formData.jobAddress || '',
      jobCity: formData.jobCity || '',
      jobState: formData.jobState || '',
      jobZipCode: formData.jobZipCode,
      owners: formData.owners || [],
      buyerId: formData.buyerId || '',
      GCs: formData.GCs || [],
      lenders: formData.lenders || [],
      apn: formData.apn || '',
      constructionMonitorPermitId: Number(formData.constructionMonitorPermitId) || null,
      estimatedMaterialAmount: formData.estimatedMaterialAmount
        ? Number(formData.estimatedMaterialAmount)
        : null,
      jobType: formData.jobType || 'commercial',
      jobSector: formData.jobSector || 'private',
      customerType: formData.customerType || 'SUB',
      jobFurnishings: formData.jobFurnishings || '',
      hasNoLender: formData.hasNoLender || false,
      isJobBonded: formData.isJobBonded || false,
      sureties: formData.sureties || [],
      isGCUnknown: formData?.isGCUnknown || false,
      isLenderUnknown: formData?.isLenderUnknown || false,
      isOwnerUnknown: formData?.isOwnerUnknown || false,
      isSurityUnknown: formData?.isSurityUnknown || false,
      ...moreDetails,
    };

    if (editMode === false) {
      const createdSupplierJob = await dispatch(
        createSupplierJob({
          supplierId: currentSupplier?.id || '',
          supplierJobDetails: { ...data },
          attachmentFiles: attachments?.attachmentFiles,
        })
      );
      if (createdSupplierJob.type === 'supplierJobs/createSupplierJob/fulfilled') {
        openDillToast({ message: 'Job successfully added!', type: 'SUCCESS' });
        handleCloseModal();
        if (onReload) {
          onReload();
        }
      } else {
        const payload = createdSupplierJob.payload as any;
        let errorMessage = 'Failed to add the job';
        if (payload) {
          errorMessage = payload;
        }
        openDillToast({ message: errorMessage, type: 'ERROR' });
      }
    } else {
      const updatedSupplierJob = await dispatch(
        updateSupplierJob({
          supplierId: currentSupplier?.id || '',
          supplierJobId: selectedSupplierJob?.id ?? '',
          supplierJobDetails: { ...data },
          attachmentFiles: attachments?.attachmentFiles,
          attachmentUrls: attachments?.attachmentUrls,
        })
      );
      if (updatedSupplierJob.type === 'supplierJobs/updateSupplierJob/fulfilled') {
        openDillToast({ message: 'Job successfully updated!', type: 'SUCCESS' });
        const selectedRequestedJob = requestedSupplierJobs.filter((job) => {
          return job.supplierJobId === selectedSupplierJob?.id;
        });
        if (selectedRequestedJob.length > 0) {
          for (const requestedJob of selectedRequestedJob) {
            await dispatch(
              updateRequestedSupplierJob({
                supplierId: currentSupplier?.id || '',
                requestedSupplierJobId: requestedJob.id,
                requestedSupplierJobDetails: { status: 'approved' },
              })
            );
            if (requestedJob?.customerAddress?.address !== undefined) {
              await dispatch(
                updateSupplierBuyer({
                  supplierId: currentSupplier?.id || '',
                  supplierBuyerId: requestedJob?.buyerId || '',
                  supplierBuyerDetails: {
                    address: requestedJob.customerAddress?.address || '',
                    city: requestedJob.customerAddress?.city || '',
                    state: requestedJob.customerAddress?.state || '',
                    zipCode: requestedJob.customerAddress?.zipCode || '',
                  },
                })
              );
              if (onReload) {
                onReload();
              }
            }
          }
          await dispatch(getRequestedSupplierJobs({ supplierId: currentSupplier?.id || '' }));
        }
        handleCloseModal();
        if (onUpdateRequestedJob) {
          onUpdateRequestedJob();
        }
        if (onReload) {
          onReload();
        }
      } else {
        openDillToast({ message: 'Failed to update job', type: 'ERROR' });
      }
    }
  };

  const onVerifyJobState = (dataToVerify: SupplierVerifiedJobFormData) => {
    setVerifyJobData(dataToVerify);
    setModalState(ModalState.VERIFY_JOB);
    setSupplierJobInput(dataToVerify);
  };

  const onVerifySubmitJobAddress = async (jobAddressData: VerifyJobRequestData) => {
    if (currentSupplier?.ownerVerificationEnabled && verifyJobCheckBox) {
      await dispatch(fetchQueuryJobSiteAddresses(jobAddressData)).then((res) => {
        const { address, city, state, zipCode, jobNumber, name, buyerId, customerJobNumber } =
          jobAddressData;
        if (res.type === 'jobOwner/fetchAddresses/fulfilled') {
          const permits = res.payload as ContructionMonitorPermitOption[];

          const queriedJobAddress = `${address}, ${city}, ${state}, ${zipCode}`;
          if (permits.length > 0) {
            // loop through results and use compareTwoStrings to queriedJobAddress to find the best match
            // best match will be the one with a score above 0.5 or null if none are above 0.5
            let bestMatch: ContructionMonitorPermitOption | null = null;
            let bestMatchScore = 0;

            for (const permit of permits) {
              const permitAddress = `${permit.jobAddress}, ${permit.jobCity}, ${permit.jobState}, ${permit.jobZipCode}`;
              const score = stringSimilarity.compareTwoStrings(queriedJobAddress, permitAddress);

              if (score > 0.5 && score > bestMatchScore) {
                bestMatch = permit;
                bestMatchScore = score;
              }
              const jobToVerify = {
                name,
                buyerId,
                owners: [
                  {
                    name: '',
                    address: '',
                    city: '',
                    state: '',
                    zipCode: '',
                  },
                ],
                jobNumber,
                customerJobNumber,
                jobAddress: address,
                jobCity: city,
                jobState: state,
                jobZipCode: zipCode,
                constructionMonitorPermitId: null,
                GCs: [
                  {
                    name: '',
                    address: '',
                    city: '',
                    state: '',
                    zipCode: '',
                  },
                ],
                lenders: [
                  {
                    name: '',
                    address: '',
                    city: '',
                    state: '',
                    zipCode: '',
                  },
                ],
                sureties: [
                  {
                    name: '',
                    address: '',
                    city: '',
                    state: '',
                    zipCode: '',
                  },
                ],
                apn: '',
                customerType: 'SUB' as const,
              };

              if (bestMatch) {
                onVerifyJobState({
                  ...jobToVerify,
                  owners: [
                    {
                      name: bestMatch.owner,
                      address: bestMatch.ownerAddress,
                      city: bestMatch.ownerCity,
                      state: bestMatch.ownerState,
                      zipCode: bestMatch.ownerZipCode,
                    },
                  ],
                  constructionMonitorPermitId: Number(bestMatch.constructionMonitorPermitId),
                  GCs: [
                    {
                      name: bestMatch.gcName,
                      address: bestMatch.gcAddress,
                      city: bestMatch.gcCity,
                      state: bestMatch.gcState,
                      zipCode: bestMatch.gcZipCode,
                    },
                  ],
                  jobType: bestMatch.jobType,
                  verifiedProperty: {
                    address: bestMatch.jobAddress,
                    city: bestMatch.jobCity,
                    state: bestMatch.jobState,
                    zipCode: bestMatch.jobZipCode,
                    apn: bestMatch.apn,
                  },
                });
              } else {
                onVerifyJobState(jobToVerify);
              }
            }
          }
        } else {
          console.error('Error fetching data: ', res);
        }
      });
    } else {
      const { address, city, state, zipCode, jobNumber, name, buyerId, customerJobNumber } =
        jobAddressData;
      const jobToVerify = {
        name,
        buyerId,
        customerJobNumber,
        owners: [
          {
            name: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
          },
        ],
        jobNumber,
        jobAddress: address,
        jobCity: city,
        jobState: state,
        jobZipCode: zipCode,
        constructionMonitorPermitId: null,
        GCs: [
          {
            name: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
          },
        ],
        lenders: [
          {
            name: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
          },
        ],
        sureties: [
          {
            name: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
          },
        ],
        apn: '',
        estimatedMaterialAmount: 0,
        jobType: 'commercial' as const,
        jobSector: 'private' as const,
        customerType: 'SUB' as const,
        jobFurnishings: '',
        hasNoLender: false,
      };

      const rrr = {
        name,
        buyerId,
        customerJobNumber,
        jobNumber,
        jobAddress: address,
        jobCity: city,
        jobState: state,
        jobZipCode: zipCode,
        //
        jobCounty: supplierJobInput.jobCounty ?? '',
        jobLegalDescription: supplierJobInput.jobLegalDescription ?? '',
        //
        owners:
          supplierJobInput.owners && supplierJobInput.owners?.length > 0
            ? supplierJobInput.owners
            : [
                {
                  name: '',
                  address: '',
                  city: '',
                  state: '',
                  zipCode: '',
                },
              ],
        GCs:
          supplierJobInput.GCs && supplierJobInput.GCs?.length > 0
            ? supplierJobInput.GCs
            : [
                {
                  name: '',
                  address: '',
                  city: '',
                  state: '',
                  zipCode: '',
                },
              ],
        lenders:
          supplierJobInput.lenders && supplierJobInput.lenders?.length > 0
            ? supplierJobInput.lenders
            : [
                {
                  name: '',
                  address: '',
                  city: '',
                  state: '',
                  zipCode: '',
                },
              ],
        constructionMonitorPermitId: supplierJobInput.constructionMonitorPermitId,
        apn: supplierJobInput.apn ?? '',
        estimatedMaterialAmount: supplierJobInput.estimatedMaterialAmount
          ? Number(supplierJobInput.estimatedMaterialAmount)
          : 0,
        jobType: supplierJobInput.jobType || 'commercial',
        jobSector: supplierJobInput.jobSector || 'private',
        customerType: supplierJobInput.customerType ?? 'SUB',
        jobFurnishings: supplierJobInput.jobFurnishings ?? '',
        hasNoLender: supplierJobInput.hasNoLender ?? false,
        isJobBonded: supplierJobInput.isJobBonded ?? false,
        sureties:
          supplierJobInput.sureties && supplierJobInput.sureties?.length > 0
            ? supplierJobInput.sureties
            : [
                {
                  name: '',
                  address: '',
                  city: '',
                  state: '',
                  zipCode: '',
                  bondNumber: '',
                },
              ],
        jobInfoSheetAttachments: selectedSupplierJob?.jobInfoSheetAttachments || [],
        isGCUnknown: selectedSupplierJob?.isGCUnknown,
        isLenderUnknown: selectedSupplierJob?.isLenderUnknown,
        isOwnerUnknown: selectedSupplierJob?.isOwnerUnknown,
        isSurityUnknown: selectedSupplierJob?.isSurityUnknown,
      };
      onVerifyJobState({ ...jobToVerify, ...rrr });
      // onVerifyJobState({ ...jobToVerify });
    }
  };
  const handleBack = () => {
    setModalState(ModalState.ADD_JOB);
  };
  const handleRequestInfoSheet = (data: any) => {
    setRequestJobInfoSheet(data);
    setModalState(ModalState.REQUEST_JOB_INFO_SHEET);
  };

  const checkEditOwner = useMemo(() => {
    if (selectedSupplierJob?.owners && selectedSupplierJob?.owners.length > 0) {
      setModalState(ModalState.VERIFY_JOB);
      return true;
    }
    setModalState(ModalState.ADD_JOB);
    return false;
  }, [selectedSupplierJob, open]);

  const handleVerifyData = async (
    data: SupplierVerifiedJobFormData,
    dataOriginal: SupplierVerifiedJobFormData
  ) => {
    setVerifying(true);
    const jobAddressData = {
      address: data?.jobAddress ?? '',
      city: data?.jobCity ?? '',
      state: data?.jobState ?? '',
      zipCode: data?.jobZipCode ?? '',
      jobNumber: data?.jobNumber ?? '',
      name: data?.name ?? '',
      buyerId: data?.buyerId ?? '',
      customerJobNumber: data?.customerJobNumber ?? '',
    };
    setOriginalJobData(dataOriginal);
    await dispatch(fetchQueuryJobSiteAddresses(jobAddressData)).then((res) => {
      const { address, city, state, zipCode, jobNumber, name, buyerId, customerJobNumber } =
        jobAddressData;
      if (res.type === 'jobOwner/fetchAddresses/fulfilled') {
        const permits = res.payload as ContructionMonitorPermitOption[];

        const queriedJobAddress = `${address}, ${city}, ${state}, ${zipCode}`;

        if (permits.length > 0) {
          // loop through results and use compareTwoStrings to queriedJobAddress to find the best match
          // best match will be the one with a score above 0.5 or null if none are above 0.5
          let bestMatch: ContructionMonitorPermitOption | null = null;
          let bestMatchScore = 0;

          for (const permit of permits) {
            const permitAddress = `${permit.jobAddress}, ${permit.jobCity}, ${permit.jobState}, ${permit.jobZipCode}`;
            const score = stringSimilarity.compareTwoStrings(queriedJobAddress, permitAddress);

            if (score > 0.5 && score > bestMatchScore) {
              bestMatch = permit;
              bestMatchScore = score;
            }
            const jobToVerify = {
              ...data,
              name,
              buyerId,
              jobNumber,
              jobAddress: address,
              jobCity: city,
              jobState: state,
              jobZipCode: zipCode,
            };

            if (bestMatch) {
              setConstructionMonitorJobData({
                ...jobToVerify,
                owners: [
                  {
                    name: bestMatch.owner,
                    address: bestMatch.ownerAddress,
                    city: bestMatch.ownerCity,
                    state: bestMatch.ownerState,
                    zipCode: bestMatch.ownerZipCode,
                  },
                ],
                constructionMonitorPermitId: Number(bestMatch.constructionMonitorPermitId),
                GCs: [
                  {
                    name: bestMatch.gcName,
                    address: bestMatch.gcAddress,
                    city: bestMatch.gcCity,
                    state: bestMatch.gcState,
                    zipCode: bestMatch.gcZipCode,
                  },
                ],
                jobType: bestMatch.jobType,
                verifiedProperty: {
                  address: bestMatch.jobAddress,
                  city: bestMatch.jobCity,
                  state: bestMatch.jobState,
                  zipCode: bestMatch.jobZipCode,
                  apn: bestMatch.apn,
                },
              });
              setModalState(ModalState.VERIFIED_JOB_DATA);
              setVerifying(false);
            } else {
              const emptyResults = {
                ...data,
                name,
                buyerId,
                owners: [
                  {
                    name: '',
                    address: '',
                    city: '',
                    state: '',
                    zipCode: '',
                  },
                ],
                jobNumber,
                jobAddress: address,
                jobCity: city,
                jobState: state,
                jobZipCode: zipCode,
                constructionMonitorPermitId: null,
                GCs: [
                  {
                    name: '',
                    address: '',
                    city: '',
                    state: '',
                    zipCode: '',
                  },
                ],
                apn: '',
                jobType: 'commercial' as const,
                jobSector: 'private' as const,
                customerType: 'SUB' as const,
                jobFurnishings: '',
              };
              setConstructionMonitorJobData(emptyResults);
              setModalState(ModalState.VERIFIED_JOB_DATA);
              setVerifying(false);
            }
          }
        }
      } else {
        console.error('Error fetching data: ', res);
        setVerifying(false);
      }
    });
  };

  const handleReviewData = async (data: SupplierVerifiedJobFormData) => {
    setSupplierJobInput(data);
    setVerifyJobData(data);
    setModalState(ModalState.VERIFY_JOB);
  };
  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <>
        {modalState === ModalState.ADD_JOB && !checkEditOwner && (
          <AddSupplierJobForm
            jobSiteData={supplierJobInput}
            onClose={handleCloseModal}
            onBuyerEdit={() => {
              setWasBuyerEdited(true);
              refetchSupplierJob();
            }}
            editMode={editMode ? 'EDIT' : 'ADD'}
            onFormSubmit={onVerifySubmitJobAddress}
            onRequestInfoSheet={handleRequestInfoSheet}
            onVerifyJobCheckBox={(val: boolean) => {
              setVerifyJobCheckBox(val);
            }}
            verifyJobCheckBox={verifyJobCheckBox}
          />
        )}
        {modalState === ModalState.VERIFY_JOB && (
          <SupplierVerifyJobForm
            {...verifyJobData}
            supplierJobData={supplierJobInput}
            onClose={handleCloseModal}
            onUseVerifiedData={handleSubmitJob}
            onManualDataUpdate={handleSubmitJob}
            onBack={handleBack}
            editMode={checkEditOwner ? 'EDIT' : 'ADD'}
            onBuyerEdit={() => {
              setWasBuyerEdited(true);
              refetchSupplierJob();
            }}
            onVerify={handleVerifyData}
            verifying={verifying}
            onRequestInfoSheet={handleRequestInfoSheet}
            verifyJobCheckBox={verifyJobCheckBox}
          />
        )}
        {modalState === ModalState.VERIFIED_JOB_DATA && (
          <CompareJobData
            onClose={handleCloseModal}
            verifyJobData={constructionMonitorData}
            onCompareData={() => {
              setModalState(ModalState.COMPARE_DATA);
            }}
            onContinueEdit={() => {
              setModalState(ModalState.VERIFY_JOB);
            }}
          />
        )}
        {modalState === ModalState.COMPARE_DATA && (
          <SupplierCompareJob
            onClose={handleCloseModal}
            verifyJobData={constructionMonitorData}
            originalJobData={originalJobData}
            onReviewData={handleReviewData}
          />
        )}
        {modalState === ModalState.REQUEST_JOB_INFO_SHEET && (
          <RequestInfoSheetForm
            onClose={handleCloseModal}
            open={open}
            data={{
              supplierId: currentSupplier?.id ?? '',
              domainId: currentSupplier?.domainId ?? '',
              jobId: selectedSupplierJob?.id ?? '',
              buyerId: requestJobInfoSheet.buyerId || '',
              buyerName: requestJobInfoSheet.buyerName || '',
              name: requestJobInfoSheet.name || '',
              jobNumber: requestJobInfoSheet.jobNumber || '',
              jobAddress: requestJobInfoSheet.jobAddress || '',
              jobCity: requestJobInfoSheet.jobCity || '',
              jobState: requestJobInfoSheet.jobState || '',
              jobZipCode: requestJobInfoSheet.jobZipCode || '',
              buyer: selectedSupplierJob?.buyer,
              selectedSupplierJob: selectedSupplierJob,
              customerJobNumber: requestJobInfoSheet.customerJobNumber || '',
              buyerAddress: requestJobInfoSheet.buyerAddress || '',
              buyerCity: requestJobInfoSheet.buyerCity || '',
              buyerState: requestJobInfoSheet.buyerState || '',
              buyerZipCode: requestJobInfoSheet.buyerZipCode || '',
            }}
          />
        )}
      </>
    </Modal>
  );
};
